/**
 * this component will display the card summary information
 *
 * @module views/components/Subscription
 * @memberof -Common
 */
import './Subscription.scss';

import React, { useCallback, useEffect, useRef } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { Price as PalPrice } from 'web-palette/dist/components/Price/Price';

import Button from '@ulta/core/components/Button/Button';
import CheckboxToggle from '@ulta/core/components/CheckboxToggle/CheckboxToggle';
import SelectField_New from '@ulta/core/components/SelectField_New/SelectField_New';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import constants from '@ulta/core/utils/constants/constants';
import { patchDataLayer, processEvents } from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Tag from '@ulta/modules/Tag/Tag';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';
import { getPdpProps } from '@ulta/utils/subscriptionUtil/subscriptionUtil';

import * as utils from '../Subscription/Subscription';
import SubscriptionInfo from '../SubscriptionInfo/SubscriptionInfo';

/**
 * Represents a Subscription component
 *
 * @method
 * @param {SubscriptionProps} props - React properties passed from composition
 * @returns Subscription
 */
export const Subscription = function( props ){
  const {
    frequencyOptions,
    frequencyOptionsAction,
    invokeAction,
    learnMoreLabel,
    learnMoreOverlay,
    listPrice,
    listPriceAccessibilityLabel,
    onSubscriptionChange,
    onSubscriptionFrequencyChange,
    salePrice,
    salePriceAccessibilityLabel,
    subsAndSaveIcon,
    subsAndSaveOptIn,
    subsAndSaveOptInAction
  } = props;

  if( !props.subsAndSaveOptIn || !props.subsAndSaveIcon || !props.salePrice || !props.listPrice ){
    return null;
  }
  const formikRef = useRef();
  const { breakpoint } = useDeviceInflection();
  const isMobile = breakpoint?.CURRENT_BREAKPOINT === 'SM';

  const getPdpPropUtil = getPdpProps( { isSubscriptionDisabled: subsAndSaveOptIn.disabled, isMobile : isMobile } );

  useEffect( () => {
    formikRef?.current?.setFieldValue( 'frequencyOptions', frequencyOptions?.value );
  }, [frequencyOptions?.value] );


  const handleCheckboxChange = useCallback(
    utils.handleSubsriptionOptInChange( { subsAndSaveOptIn, frequencyOptions, subsAndSaveOptInAction },
      { onSubscriptionChange }
    ), [subsAndSaveOptIn, onSubscriptionChange] );

  const handleSelectChange = useCallback(
    utils.handleSubsriptionFrequencyChange( { subsAndSaveOptIn, frequencyOptions, frequencyOptionsAction },
      { onSubscriptionFrequencyChange }
    ), [subsAndSaveOptIn, onSubscriptionFrequencyChange] );


  return (

    <div className='Subscription'>
      <Formik initialValues={ { frequencyOptions: frequencyOptions?.value } }
        innerRef={ formikRef }
      >
        <Form>
          <div className='Subscription__Container'>
            <div className='Subscription__CheckBox'>
              <CheckboxToggle
                id={ subsAndSaveOptIn.name }
                name={ subsAndSaveOptIn.name }
                toggleButton={ false }
                defaultChecked={ false }
                disabled={ subsAndSaveOptIn.disabled }
                checked={ subsAndSaveOptIn.value }
                ariaDescribedBy='subscriptionSublabel'
                className='pal-c-Checkbox--fullwidth'
                checkboxContainerWidth='2rem'
                checkboxVerticalPosition='top'
                onChange={ () => {
                  handleCheckboxChange();
                } }
              >
                <div className='Subscription__Label' >
                  <Tag
                    variant='icon'
                    message={ subsAndSaveOptIn.label }
                    icon={ subsAndSaveIcon }
                    size='large'
                    iconColor={ getPdpPropUtil.tagIconColor }
                    textColor={ getPdpPropUtil.tagTextColor }
                  />
                  <span className='Subscription__Separator' > &nbsp; </span>
                  <PalPrice
                    listPrice={ listPrice }
                    listPriceAccessibilityLabel={ interpolateString( listPriceAccessibilityLabel, [listPrice] ) }
                    salePrice={ salePrice }
                    salePriceAccessibilityLabel={ interpolateString( salePriceAccessibilityLabel, [salePrice] ) }
                    disabled={ subsAndSaveOptIn.disabled }
                    variant='default'
                  />
                </div>
              </CheckboxToggle>
            </div>
            <div className='Subscription__Row'>
              <div id='subscriptionSublabel'>
                <Text
                  color='neutral-500'
                  textStyle='body-3'
                  htmlTag='p'
                >
                  { subsAndSaveOptIn.helpText }
                </Text>
              </div>
              <Button
                className='Subscription__LearnMore'
                variant='link'
                size='default'
                overlayTitle={ { text: learnMoreOverlay?.title, htmlTag: 'h1', textStyle: 'title-lg' } }
                label={ learnMoreLabel?.label }
                action={ {
                  ...learnMoreLabel,
                  navigationType: getPdpPropUtil.buttonNavigationType
                } }
                overlayContent={
                  <SubscriptionInfo
                    { ...spreadProps( { ...learnMoreOverlay } ) }
                    invokeAction={ invokeAction }
                  />
                }
              />
            </div>
          </div>
          {
            !subsAndSaveOptIn.disabled &&
            subsAndSaveOptIn.value &&
            <div className='Subscription__FrequencyOptions'
              data-testid='ShipsEvery'
            >
              <SelectField_New
                name='frequencyOptions'
                label={ frequencyOptions.label }
                options={ frequencyOptions.options }
                value={ frequencyOptions.value }
                enabled={ true }
                onChange={ handleSelectChange }
              />
            </div>
          }
        </Form>
      </Formik>
    </div>
  );
};

/**
 * handleSubsriptionOptInChange function for handling the checkbox change
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 */
export const handleSubsriptionOptInChange = ( data, method ) => () => {
  const { subsAndSaveOptIn, frequencyOptions, subsAndSaveOptInAction } = data;
  const { onSubscriptionChange } = method;
  const isSubscriptionOpted = subsAndSaveOptIn.value;
  subsAndSaveOptIn.value = !isSubscriptionOpted;
  utils.invokeSubscriptionMethodDataCapture( { dataCaptureData : subsAndSaveOptInAction?.dataCaptureData, values: { isSubscribed: subsAndSaveOptIn.value, subscriptionFrequency: frequencyOptions.value } } );
  onSubscriptionChange( !isSubscriptionOpted );
};

/**
 * handleSubsriptionFrequencyChange function for handling the dropdown frequency change
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 */
export const handleSubsriptionFrequencyChange = ( data, method ) => ( eventData ) => {
  const { subsAndSaveOptIn, frequencyOptions, frequencyOptionsAction } = data;
  const { onSubscriptionFrequencyChange } = method;
  frequencyOptions.value = eventData;
  utils.invokeSubscriptionMethodDataCapture( { dataCaptureData : frequencyOptionsAction?.dataCaptureData, values: { isSubscribed: subsAndSaveOptIn.value, subscriptionFrequency: frequencyOptions.value } } );
  onSubscriptionFrequencyChange( eventData );
};

/**
 * Represents a Subscription component datacapture functionality to process derived value events
 *
 * @method
 * @param {object} data - function parameters passed from event handler
 */
export const invokeSubscriptionMethodDataCapture = ( data ) => {
  const { values, dataCaptureData } = data || {};
  if( !dataCaptureData || !values ){
    return;
  }
  const patchedData = patchDataLayer( { dataCaptureData, values } );
  processEvents( { dataCapture: { ...patchedData } }, constants.DATACAPTURE_EVENT_TYPE.click );
};

/**
* Property type definitions
  * @typedef SubscriptionProps
  * @type {object}
  * @property {array} frequencyOptions - Sets the Subscription Frequency values for the dropdown
  * @property {object} frequencyOptionsAction - Sets the frequencyOptionsAction properties which sets up the data capture props
  * @property {func} invokeAction - for triggering graphql calls
  * @property {object} learnMoreLabel - props for learn more button action
  * @property {object} learnMoreOverlay - props for learn more overlay
  * @property {string} listPrice - Sets the list price to display
  * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
  * @property {string} salePrice - Sets the sale price to display
  * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
  * @property {string} subsAndSaveIcon - Sets value for an icon
  * @property {object} subsAndSaveOptIn - Set the checkbox and Label and help text
  * @property {object} subsAndSaveOptInAction - Sets the subsAndSaveOptInAction properties which sets up the data capture props
*/
export const propTypes =  {
  frequencyOptions: PropTypes.object,
  frequencyOptionsAction: PropTypes.object,
  invokeAction: PropTypes.func,
  learnMoreLabel: PropTypes.object,
  learnMoreOverlay: PropTypes.object,
  listPrice: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string,
  salePrice: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string,
  subsAndSaveIcon: PropTypes.string,
  subsAndSaveOptIn: PropTypes.object,
  subsAndSaveOptInAction: PropTypes.object
};

Subscription.propTypes = propTypes;
export default Subscription;